import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Bottom-anchored cookie banner. GDPR + ePrivacy compliant:
 * - Equal visual weight for "Accetta" and "Rifiuta" buttons.
 * - Third option "Personalizza" opens granular preferences.
 * - No pre-ticked non-essential categories (nothing gets set until user chooses).
 */
export function CookieBanner({ open, onAcceptAll, onRejectAll, onPreferences }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 0.9, 0.24, 1] }}
          role="dialog"
          aria-live="polite"
          aria-label="Preferenze sui cookie"
          data-testid="cookie-banner"
          className="fixed inset-x-3 sm:inset-x-6 bottom-3 sm:bottom-6 z-[70] max-w-3xl md:mx-auto"
        >
          <div className="bg-foreground text-background shadow-2xl border border-secondary/25">
            <div className="p-6 sm:p-8 grid gap-6 md:grid-cols-[auto,1fr,auto] md:items-center">
              <div className="hidden md:flex h-12 w-12 items-center justify-center bg-secondary/15 border border-secondary/25 shrink-0">
                <Cookie className="h-5 w-5 text-secondary" />
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-[0.32em] font-semibold text-secondary">
                  Cookie & Privacy
                </p>
                <p className="mt-3 text-sm sm:text-[15px] leading-relaxed text-background/85">
                  Usiamo cookie tecnici, necessari al funzionamento del sito, e — solo con il tuo consenso —
                  cookie di statistica e marketing per capire come migliorare l'esperienza e valutare le nostre campagne.
                  Puoi accettare, rifiutare o scegliere quali categorie attivare. Dettagli nella{" "}
                  <Link
                    to="/cookie"
                    className="underline underline-offset-2 text-secondary hover:text-secondary/80"
                    data-testid="banner-link-cookie"
                  >
                    Cookie Policy
                  </Link>
                  {" "}e nella{" "}
                  <Link
                    to="/privacy"
                    className="underline underline-offset-2 text-secondary hover:text-secondary/80"
                    data-testid="banner-link-privacy"
                  >
                    Privacy Policy
                  </Link>.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[190px]">
                <button
                  type="button"
                  onClick={onAcceptAll}
                  data-testid="cookie-accept-all"
                  className="btn-base bg-secondary text-foreground px-6 py-3 hover:bg-secondary/85 text-sm"
                >
                  Accetta tutti
                </button>
                <button
                  type="button"
                  onClick={onRejectAll}
                  data-testid="cookie-reject-all"
                  className="btn-base border border-background/30 text-background px-6 py-3 hover:bg-background hover:text-foreground text-sm"
                >
                  Rifiuta
                </button>
                <button
                  type="button"
                  onClick={onPreferences}
                  data-testid="cookie-preferences"
                  className="btn-base text-background/75 hover:text-background px-6 py-2 text-xs underline underline-offset-2"
                >
                  Personalizza
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
